<template>
  <div ref="root" class="design-picker" :class="{ 'is-single-column': columns === 1, 'without-icons': !icons }" @focusout="onFocusOut">
    <button ref="trigger" class="design-picker-trigger" type="button" data-press-intent="custom"
      aria-haspopup="listbox" :aria-expanded="open" :aria-controls="listId"
      :aria-label="`${label}, ${active.label}`" @click="toggle" @keydown="onTriggerKey">
      <ExhibitionFeatureIcon v-if="icons" :feature="active.id" />
      <span class="design-picker-current"><span><slot name="group-label" :group="activeGroup">{{ activeGroup.title }}</slot></span><strong>{{ active.label }}</strong></span>
      <svg class="design-picker-chevron" :class="{ 'is-open': open }" viewBox="0 0 20 20" aria-hidden="true"><path d="m6 8 4 4 4-4" /></svg>
    </button>
    <Transition name="design-picker" :css="!keyboardInteraction">
      <div v-if="open" :id="listId" ref="menu" class="design-picker-menu" :class="{ 'opens-up': opensUp }"
        :style="{ '--picker-space': `${availableSpace}px` }" role="listbox" :aria-label="label" @keydown="onMenuKey">
        <div v-for="group in studies" :key="group.id" class="design-picker-group" role="group" :aria-labelledby="`${listId}-${group.id}`">
          <div :id="`${listId}-${group.id}`" class="design-picker-group-label"><slot name="group-label" :group="group">{{ group.title }}</slot></div>
          <div class="design-picker-options">
            <button v-for="feature in group.features" :key="feature.id" type="button" role="option" data-press-intent="custom"
              :data-feature="feature.id" :aria-selected="value === feature.id" :tabindex="focusedId === feature.id ? 0 : -1"
              @focus="focusedId = feature.id" @click="choose(feature.id)">
              <ExhibitionFeatureIcon v-if="icons" :feature="feature.id" /><span>{{ feature.label }}<small v-if="feature.description">{{ feature.description }}</small></span>
              <svg class="design-picker-check" :class="{ 'is-selected': value === feature.id }" viewBox="0 0 16 16" aria-hidden="true"><path d="m3 8 3 3 7-7" /></svg>
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import ExhibitionFeatureIcon from './ExhibitionFeatureIcon.vue';

export default {
  components: { ExhibitionFeatureIcon },
  props: { studies: { type: Array, required: true }, value: { type: String, required: true }, label: { type: String, default: 'Design detail' }, icons: { type: Boolean, default: true }, columns: { type: Number, default: 2 } },
  emits: ['select', 'open-change'],
  data: () => ({ open: false, focusedId: '', opensUp: false, availableSpace: 400, keyboardInteraction: false }),
  computed: {
    items() { return this.studies.flatMap(group => group.features); },
    active() { return this.items.find(feature => feature.id === this.value) || this.items[0]; },
    activeGroup() { return this.studies.find(group => group.features.some(feature => feature.id === this.active.id)); },
    listId() { return `exhibition-design-options-${this.$.uid}`; },
  },
  watch: { open(value) { this.$emit('open-change', value); } },
  mounted() {
    document.addEventListener('pointerdown', this.onOutsidePointer, true);
    window.addEventListener('resize', this.onResize);
  },
  beforeUnmount() {
    document.removeEventListener('pointerdown', this.onOutsidePointer, true);
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    async show(keyboard = false) {
      this.keyboardInteraction = keyboard;
      this.focusedId = this.active.id;
      const rect = this.$refs.trigger.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom - 12;
      const above = rect.top - 84;
      const expectedHeight = this.studies.reduce((height, group) => height + Math.ceil(group.features.length / this.columns) * (group.features.some(feature => feature.description) ? 64 : 46) + 38, 16);
      this.opensUp = below < expectedHeight && above > below;
      this.availableSpace = Math.max(120, this.opensUp ? above : below);
      this.open = true;
      await this.$nextTick();
      if (keyboard) this.focusOption(this.active.id);
    },
    toggle() { if (this.open) this.close(); else this.show(); },
    close(restoreFocus = false) {
      this.open = false;
      if (restoreFocus) this.$refs.trigger.focus({ preventScroll: true });
    },
    choose(id) { this.$emit('select', id); this.close(true); },
    focusOption(id) {
      this.focusedId = id;
      this.$refs.menu?.querySelector(`[data-feature="${id}"]`)?.focus({ preventScroll: true });
    },
    onTriggerKey(event) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        if (this.open) this.close(true); else this.show(true);
      } else if (event.key === 'Escape' && this.open) { event.preventDefault(); this.close(true); }
    },
    onMenuKey(event) {
      this.keyboardInteraction = true;
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.close(true); return; }
      const index = this.items.findIndex(feature => feature.id === this.focusedId);
      const step = { ArrowDown: this.columns, ArrowUp: -this.columns, ArrowRight: 1, ArrowLeft: -1 }[event.key];
      let next = step == null ? null : Math.max(0, Math.min(this.items.length - 1, index + step));
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.items.length - 1;
      if (next != null) { event.preventDefault(); this.focusOption(this.items[next].id); }
      else if (event.key.length === 1 && /\S/.test(event.key)) {
        const ordered = [...this.items.slice(index + 1), ...this.items.slice(0, index + 1)];
        const match = ordered.find(feature => feature.label.toLowerCase().startsWith(event.key.toLowerCase()));
        if (match) { event.preventDefault(); this.focusOption(match.id); }
      }
    },
    onOutsidePointer(event) { if (this.open && !this.$refs.root.contains(event.target)) this.close(); },
    onFocusOut(event) { if (event.relatedTarget && !this.$refs.root.contains(event.relatedTarget)) this.close(); },
    onResize() { if (this.open) this.close(); },
  },
};
</script>

<style scoped>
.design-picker { position:relative; width:100%; color:var(--case-ink); }
.design-picker button { margin:0; color:inherit; font:inherit; cursor:var(--site-interactive-cursor,pointer); user-select:none; -webkit-tap-highlight-color:transparent; }
.design-picker-trigger { display:flex; align-items:center; gap:12px; width:100%; min-height:64px; padding:11px 14px; border:1px solid var(--case-rule); border-radius:4px; background:color-mix(in srgb,var(--case-ink) 3%,transparent); text-align:left; }
.design-picker-trigger > .exhibition-feature-icon { width:26px; height:26px; flex:none; }
.design-picker-current { display:grid; gap:3px; min-width:0; flex:1; font:12px/1.25 'Quattrocento Sans',sans-serif; color:var(--case-muted); }
.design-picker-current strong { font-size:16px; font-weight:400; color:var(--case-ink); }
.design-picker-chevron { width:18px; height:18px; flex:none; fill:none; stroke:currentColor; stroke-width:1.3; transition:transform 140ms cubic-bezier(.22,1,.36,1); }
.design-picker-chevron.is-open { transform:rotate(180deg); }
.design-picker-trigger:active { background:color-mix(in srgb,var(--case-ink) 6%,transparent); }
.design-picker-menu { position:absolute; z-index:5; inset:calc(100% + 7px) 0 auto; max-height:min(400px,var(--picker-space)); overflow-y:auto; overscroll-behavior:contain; padding:8px; border:1px solid var(--case-rule); border-radius:5px; background:var(--case-nav-bg); backdrop-filter:blur(16px); box-shadow:0 8px 24px rgb(0 0 0 / 9%); transform-origin:top; }
.design-picker-menu.opens-up { inset:auto 0 calc(100% + 7px); transform-origin:bottom; }
.design-picker-group + .design-picker-group { margin-top:12px; }
.design-picker-group-label { padding:8px 9px; color:var(--case-muted); font:12px/1.35 'Quattrocento Sans',sans-serif; }
.design-picker-options { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); }
.is-single-column .design-picker-options { grid-template-columns:minmax(0,1fr); }
.without-icons .design-picker-options button { grid-template-columns:minmax(0,1fr) 12px; }
.design-picker-options small { display:block;margin-top:4px;color:var(--case-muted);font:12px/1.4 'Quattrocento Sans',sans-serif; }
.design-picker-options button { display:grid; grid-template-columns:20px minmax(0,1fr) 12px; align-items:center; gap:7px; min-height:46px; padding:10px 8px; border:0; border-radius:3px; background:transparent; text-align:left; font:14px/1.2 'Quattrocento Sans',sans-serif; }
.design-picker-options button > .exhibition-feature-icon { width:20px; height:20px; }
.design-picker-options button[aria-selected=true] { background:color-mix(in srgb,var(--case-ink) 7%,transparent); }
.design-picker-check { width:12px; height:12px; fill:none; stroke:currentColor; stroke-width:1.4; opacity:0; }
.design-picker-check.is-selected { opacity:1; }
.design-picker button:focus-visible { outline:1px solid currentColor; outline-offset:-3px; }
.design-picker-enter-active, .design-picker-leave-active { transition:opacity 140ms cubic-bezier(.22,1,.36,1),transform 140ms cubic-bezier(.22,1,.36,1); }
.design-picker-enter-from, .design-picker-leave-to { opacity:0; transform:translateY(-4px); }
.design-picker-menu.opens-up.design-picker-enter-from, .design-picker-menu.opens-up.design-picker-leave-to { transform:translateY(4px); }
@media(hover:hover) and (pointer:fine) { .design-picker-options button:hover:not([aria-selected=true]) { background:color-mix(in srgb,var(--case-ink) 3%,transparent); } }
@media(prefers-reduced-motion:reduce) { .design-picker-chevron, .design-picker-enter-active, .design-picker-leave-active { transition:none; } }
</style>
